import { useCallback, useEffect } from 'react';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import useCacheProvider from '@atlassian/jira-servicedesk-reports-common/src/common/services/cache-provider/main.tsx';
import getSummaryAsync from '@atlassian/jira-servicedesk-reports-common/src/common/services/fetch-summary-async/get/index.tsx';
import { TIME_SCALES } from '@atlassian/jira-servicedesk-reports-common/src/common/summary/constants.tsx';
import { transformSummarySource as transformSummarySourceDI } from '@atlassian/jira-servicedesk-reports-common/src/common/summary/main.tsx';
import type { ChartData } from '@atlassian/jira-servicedesk-reports-common/src/common/summary/types.tsx';
import type { ReportAppProps } from '@atlassian/jira-servicedesk-reports-common/src/ui/date-filter/types.tsx';
import { get as onFetchDrillDownDI } from '../services/drilldown/get/index.tsx';
import type { IssueTableData } from '../services/drilldown/types.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSummaryError = (error: any) => {
	trackFetchErrors({
		error,
		id: 'fetchKbRequestsSummary',
		packageName: 'jiraServicedeskKbRequestsReports',
		sendToPrivacyUnsafeSplunk: true,
	});
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDrillDownError = (error: any) => {
	trackFetchErrors({
		error,
		id: 'fetchKbRequestsDrilldown',
		packageName: 'jiraServicedeskKbRequestsReports',
		sendToPrivacyUnsafeSplunk: true,
	});
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	baseUrl,
	routerProps,
	projectKey,
	// @ts-expect-error - TS2339 - Property 'onFetchDrillDown' does not exist on type 'ReportAppProps'.
	onFetchDrillDown = onFetchDrillDownDI,
	// @ts-expect-error - TS2339 - Property 'onFetchSummary' does not exist on type 'ReportAppProps'.
	onFetchSummary = getSummaryAsync,
	// @ts-expect-error - TS2339 - Property 'transformSummarySource' does not exist on type 'ReportAppProps'.
	transformSummarySource = transformSummarySourceDI,
}: ReportAppProps) => {
	const { reportId } = routerProps;
	const intl = useIntl();

	const transformKbSource = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'source' implicitly has an 'any' type.
		(source) =>
			transformSummarySource(
				source,
				{
					timescaleId: Number(
						routerProps.timescaleId !== undefined ? routerProps.timescaleId : TIME_SCALES.past7Days,
					),
					timeBreakdown: routerProps.timeBreakdown,
				},
				intl,
			),
		// Can include `intl` in dependency after https://jdog.jira-dev.com/browse/JFP-3184
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[routerProps.timeBreakdown, routerProps.timescaleId, transformSummarySource],
	);

	const summaryResource = useCacheProvider<ChartData>({
		baseUrl,
		projectKey,
		onFetch: onFetchSummary,
		initialLoadingState: true,
		// @ts-expect-error - TS2322 - Type '(error: any) => void' is not assignable to type '(arg1?: any) => Promise<undefined> | undefined'.
		onError: onSummaryError,
		onTransformSource: transformKbSource,
	});

	const drilldownResource = useCacheProvider<IssueTableData>({
		baseUrl,
		projectKey,
		onFetch: onFetchDrillDown,
		initialLoadingState: false,
		// @ts-expect-error - TS2322 - Type '(error: any) => void' is not assignable to type '(arg1?: any) => Promise<undefined> | undefined'.
		onError: onDrillDownError,
	});

	const { resetProvider: resetSummaryProvider } = summaryResource;
	const { resetProvider: resetDrillDownProvider } = drilldownResource;

	useEffect(() => {
		resetSummaryProvider();
		resetDrillDownProvider();
	}, [reportId, resetSummaryProvider, resetDrillDownProvider]);

	return { summaryResource, drilldownResource };
};
